import { defaultAppTexts } from '../contexts/AppTextsContext/defaultAppTexts';
import { fallbackLanguage } from '../../../../functions/src/shared';
import { useFirebase } from '../firebase/context';
import { AppTexts } from '../../../../functions/src/shared';
import merge from 'lodash/merge';
import { useMemo } from 'react';
import { useLanguage } from './useLanguage';

type Language = keyof typeof defaultAppTexts;

export const useDefaultAppTexts = (): AppTexts => {
  const safeLanguage = fallbackLanguage as Language;
  return defaultAppTexts[safeLanguage];
};

export const useAppTexts = (): AppTexts => {
  const { publicConfig } = useFirebase();
  const { language, languageSettingsEnabled } = useLanguage();

  return useMemo(() => {
    // Get the default texts for the selected language
    const defaultTexts = defaultAppTexts[language as Language];

    if (languageSettingsEnabled) {
      // When multi-language is enabled, check for per-locale overrides
      const localeOverrides = publicConfig?.textsByLocale?.[language];
      if (localeOverrides) {
        // Merge default texts with locale-specific overrides
        return merge({}, defaultTexts, localeOverrides);
      }
      // If no locale-specific overrides, return default texts
      return defaultTexts;
    } else {
      // When multi-language is disabled, use the legacy texts override
      return merge({}, defaultTexts, publicConfig?.texts || {});
    }
  }, [language, publicConfig?.texts, publicConfig?.textsByLocale, languageSettingsEnabled]);
};

/**
 * Interpolate double-braced template strings
 * @param text
 * @param itemsToInterpolate a map of key/value pairs of strings that need replacing
 */
export const interpolateAppText = (
  text: string,
  itemsToInterpolate: Record<string, string | undefined>,
) => {
  if (typeof itemsToInterpolate !== 'object') return text;

  let interpolated = text;

  for (const [key, value] of Object.entries(itemsToInterpolate)) {
    const regex = new RegExp('{{(?:\\s+)?(' + key + ')(?:\\s+)?}}');
    interpolated = interpolated.replace(regex, value ?? '');
  }

  return interpolated;
};
